import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quote = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    zipCode: '',
    insuranceTypes: [],
    currentInsuranceStatus: '',
    preferredContact: 'email',
    consultationCall: false,
    notes: ''
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'insuranceTypes') {
      setFormData(prev => ({
        ...prev,
        insuranceTypes: checked 
          ? [...prev.insuranceTypes, value]
          : prev.insuranceTypes.filter(type => type !== value)
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });
    
    if (validFiles.length + files.length <= 3) {
      setFiles(prev => [...prev, ...validFiles]);
    } else {
      alert('Maximum 3 files allowed');
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        if (key === 'insuranceTypes') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add files
      files.forEach((file, index) => {
        formDataToSend.append(`file${index}`, file);
      });
      
      formDataToSend.append('type', 'quote');

      const response = await axios.post(`${API}/quote`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        zipCode: '',
        insuranceTypes: [],
        currentInsuranceStatus: '',
        preferredContact: 'email',
        consultationCall: false,
        notes: ''
      });
      setFiles([]);
    } catch (error) {
      setSubmitStatus('error');
      console.error('Quote form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-light py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="page-title">Your protection starts here.</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Getting a quote is quick and easy. Share a few details, upload your declarations page, 
            and we'll compare options to make sure you're getting the best value.
          </p>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg">
              
              {/* Personal Information */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-primary-blue mb-6">Personal Information</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                      placeholder="First and Last Name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                      placeholder="12345"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="preferredContact" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    id="preferredContact"
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>
              </div>

              {/* Insurance Types */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-primary-blue mb-6">Types of Insurance Needed *</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { value: 'home', label: 'Home Insurance', icon: '🏠' },
                    { value: 'auto', label: 'Auto Insurance', icon: '🚗' },
                    { value: 'renters', label: 'Renters Insurance', icon: '🔑' },
                    { value: 'liability', label: 'Liability Insurance', icon: '🛡️' },
                    { value: 'life', label: 'Life Insurance', icon: '❤️' }
                  ].map((insurance) => (
                    <label key={insurance.value} className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        name="insuranceTypes"
                        value={insurance.value}
                        checked={formData.insuranceTypes.includes(insurance.value)}
                        onChange={handleChange}
                        className="mr-3 text-accent-orange focus:ring-accent-orange"
                      />
                      <span className="text-2xl mr-3">{insurance.icon}</span>
                      <span className="font-medium">{insurance.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Current Insurance Status */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-primary-blue mb-6">Current Insurance Status</h2>
                <div className="space-y-3">
                  {[
                    { value: 'current', label: 'Yes, I have current insurance' },
                    { value: 'new', label: 'No, I need new coverage' },
                    { value: 'switching', label: 'I\'m looking to switch providers' }
                  ].map((status) => (
                    <label key={status.value} className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="currentInsuranceStatus"
                        value={status.value}
                        checked={formData.currentInsuranceStatus === status.value}
                        onChange={handleChange}
                        className="mr-3 text-accent-orange focus:ring-accent-orange"
                      />
                      <span>{status.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-primary-blue mb-6">Upload Declarations Page (Optional)</h2>
                <p className="text-gray-600 mb-4">
                  Upload your current insurance declarations page to get a more accurate quote
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="fileUpload"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="fileUpload" className="cursor-pointer">
                    <div className="text-4xl text-gray-400 mb-4">📄</div>
                    <p className="text-lg font-medium text-gray-700 mb-2">Choose Files</p>
                    <p className="text-sm text-gray-500">PDF, JPEG, or PNG files up to 10MB each (max 3 files)</p>
                  </label>
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Information */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-primary-blue mb-6">Additional Information</h2>
                
                <div className="mb-6">
                  <label className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consultationCall"
                      checked={formData.consultationCall}
                      onChange={handleChange}
                      className="mr-3 text-accent-orange focus:ring-accent-orange"
                    />
                    <span>I'd like to schedule a consultation call to discuss my insurance needs</span>
                  </label>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes or Questions
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-orange focus:border-transparent"
                    placeholder="Any specific questions or details you'd like us to know..."
                  />
                </div>
              </div>

              {/* Consent */}
              <div className="mb-8">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    className="mt-1 text-accent-orange focus:ring-accent-orange"
                  />
                  <span className="text-sm text-gray-600">
                    I consent to be contacted by Here Comes Protection via phone, email, or text regarding my insurance needs. *
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.insuranceTypes.length === 0}
                  className="btn-primary text-lg px-12 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting Quote Request...' : 'Get My Free Quote'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-6 p-6 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
                  <div className="text-2xl mb-2">✅</div>
                  <h3 className="font-bold mb-2">Thanks! Your info is on its way to our licensed team.</h3>
                  <p>We'll review your declarations page and contact you shortly with your personalized quote.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
                  <div className="text-2xl mb-2">❌</div>
                  <h3 className="font-bold mb-2">Failed to submit quote request</h3>
                  <p>Please try again or call us directly at (972) 292-8640</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="section-dark py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help or Have Questions?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Our licensed professionals are here to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:(972)292-8640" 
              className="btn-primary text-lg px-8 py-4"
            >
              📞 Call (972) 292-8640
            </a>
            <a 
              href="https://calendly.com/g-garza-insure/herecomeprotection" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary text-lg px-8 py-4 text-white border-white hover:bg-white hover:text-deep-navy"
            >
              📅 Schedule a Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;